<template>
  <Tabs v-model:value="activeTabKey" class="shipment-rate-shop-tabs">
    <TabPanels>
      <TabPanel v-for="tab in tabs" :key="tab.key" :value="tab.key">
        <component
          :is="tab.component"
          v-if="tab.component"
          v-model:service-type-filters="serviceTypeFilters"
          :tab="tab"
          :shipment="shipment"
          :rate-shop="rateShop"
          :rate-shop-loading="rateShopLoading"
          :spot-markets="spotMarkets"
          :financial-status="financialStatus"
          :waterfall-tender="waterfallTender"
          :freight-allowance="freightAllowance"
          :disabled="disabled"
          :active-loadboards="loadBoards"
          @command="emit('command', $event)"
          @edit-brokerage-quote="emit('edit-brokerage-quote', $event)"
          @edit-quote="emit('edit-quote', $event)"
          @edit-spot-quote="emit('edit-spot-quote', $event)"
          @rate-selected="emit('rate-selected', $event)"
          @rate-shop-completed="emit('rate-shop-completed')"
          @remove-loadboard="emit('remove-loadboard', $event)"
          @send-to-brokerage="emit('send-to-brokerage', $event)"
          @spot-market-cancelled="emit('spot-market-cancelled', $event)"
          @spot-market-resent="emit('spot-market-resent', $event)"
          @update:freight-allowance="emit('update:freight-allowance', $event)"
        />
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, provide, toRefs } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import { ActionCommand, Loadable } from '@rygen/rygen-ui/model';

import {
  FreightAllowance,
  Loadboard,
  LoadboardSource,
  RateQuote,
  RateShop,
  type ServiceTypeFilters,
  Shipment,
  ShipmentAction,
  ShipmentFinancialStatus,
  ShipmentStatus,
  SpotMarket,
  WaterfallTender,
} from '@/model';
import { isStatusEqualOrAfter, isStatusEqualOrBefore } from '@/util';

import {
  ShipmentRateShopTab,
  TabKey,
} from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';

const activeTabKey = defineModel('activeTabKey', {
  type: String as PropType<TabKey>,
  default: () => 'RATES',
});

const serviceTypeFilters = defineModel('serviceTypeFilters', {
  type: Object as PropType<ServiceTypeFilters>,
  default: () => ({}),
});

const props = defineProps({
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  tabs: {
    type: Array as PropType<ShipmentRateShopTab[]>,
    default: () => [],
  },
  spotMarkets: {
    type: Object as PropType<Loadable<SpotMarket[]>>,
    default: () => ({ loading: false }),
  },
  financialStatus: {
    type: String as PropType<ShipmentFinancialStatus | undefined>,
    default: () => undefined,
  },
  waterfallTender: {
    type: Object as PropType<WaterfallTender | undefined>,
    default: () => undefined,
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  loadBoards: {
    type: Object as PropType<Loadable<Loadboard[]> | undefined>,
    default: () => undefined,
  },
});
const {
  shipment,
  rateShop,
  rateShopLoading,
  tabs,
  spotMarkets,
  financialStatus,
  waterfallTender,
  freightAllowance,
  disabled,
  loadBoards,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<ShipmentAction>): void;
  (event: 'edit-brokerage-quote', rateQuote: RateQuote): void;
  (event: 'edit-quote', rateQuote: RateQuote): void;
  (event: 'edit-spot-quote', rateQuote: RateQuote): void;
  (event: 'rate-selected', rateQuote: RateQuote): void;
  (event: 'rate-shop-completed'): void;
  (event: 'remove-loadboard', source: LoadboardSource): void;
  (event: 'send-to-brokerage', rateQuote?: RateQuote): void;
  (event: 'spot-market-cancelled', spotMarketId: number): void;
  (event: 'spot-market-resent', spotMarketId: number): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const canUserEditRateAfterTendering = computed(() => {
  if (
    !isStatusEqualOrBefore(
      financialStatus?.value,
      ShipmentFinancialStatus.INVOICE_DISCREPANCY,
    ) ||
    isStatusEqualOrAfter(shipment.value.status, ShipmentStatus.COMPLETE)
  ) {
    return false;
  }
  if (authorization.hasAuthority('shipment', 'updateLockedFields')) {
    return true;
  }
  if (
    isStatusEqualOrBefore(shipment.value.status, ShipmentStatus.BOOKED) &&
    authorization.hasAuthority('shipment', 'updateBookedShipmentLocations')
  ) {
    return true;
  }
  return false;
});
provide('canUserEditRateAfterTendering', canUserEditRateAfterTendering);
</script>

<style>
.shipment-rate-shop-tabs.p-tabs {
  --p-tabs-tabpanel-padding: 0;
}
</style>
